import type { FC, ReactNode } from "react"
import { useCallback, useMemo } from "react"

import { FinanceType, FundingGroup } from "@logic/lib/constants/finance"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useRecoilValue, useSetRecoilState } from "recoil"

import {
  useContractPackage,
  useRetrySupplementalSend,
} from "~/api/hooks/useContractPackage"
import { useContracts } from "~/containers/Proposal/state/contracts"
import {
  contractReadinessState,
  designRemoteState,
  poisonedDesignState,
} from "~/containers/Proposal/state/designs/design-state"
import { brochureJumpToSlideState } from "~/containers/Proposal/state/jump-to-slide-state"
import { isTpoProductSelectedState } from "~/containers/Proposal/state/loan-products/tpo-loan-product"
import { useProposalContext } from "~/containers/Proposal/state/proposal/proposal-context"
import { poisonedQuoteRemoteState } from "~/containers/Proposal/state/quotes/quotes-state"
import { useCanEditProposal } from "~/hooks/useCanEditProposal"

import { ContractStatusModal } from "./ContractStatusModal"
import { buildPendingVersionAlert } from "./pending-version-alert"

interface ContractStatusModalContainerProps {
  isOpen: boolean
  onClose: () => void
}

/** The gateway sends a usable sentence; fall back only when it does not. */
const describeSupplementalActionError = (error: unknown): string | null => {
  if (!error) {
    return null
  }

  const serverMessage = (
    error as { response?: { data?: { message?: unknown } } }
  ).response?.data?.message

  if (typeof serverMessage === `string` && serverMessage.trim() !== ``) {
    return serverMessage
  }

  return `That did not go through. Try again in a moment.`
}

export const ContractStatusModalContainer: FC<
  ContractStatusModalContainerProps
> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const proposal = useProposalContext()
  const canEdit = useCanEditProposal()
  const navigate = useNavigate()

  const {
    loading,
    contracts,
    versionHistory,
    requestContract,
    resendContract,
    sendSupplementalContract,
    resendingContractId,
    sendingSupplementalId,
    resendSuccess,
    sendSupplementalSuccess,
    clearResendSuccess,
    clearSendSupplementalSuccess,
    refetchContracts,
  } = useContracts()

  const poisonedDesign = useRecoilValue(poisonedDesignState)
  const poisonedQuote = useRecoilValue(poisonedQuoteRemoteState)
  const isTpoProductSelected = useRecoilValue(isTpoProductSelectedState)
  const contractReadiness = useRecoilValue(contractReadinessState)
  const remoteDesign = useRecoilValue(designRemoteState)
  const setJumpTo = useSetRecoilState(brochureJumpToSlideState)

  const { slug } = proposal.remote
  const proposalTotalCost = proposal.remote.proposalCost?.totalCost
  const isConcert =
    proposal.local.financeType === `concert` ||
    proposal.remote.selectedLoanProduct?.provider === `concert`
  const isEnfin =
    proposal.local.financeType === FinanceType.ENFIN ||
    proposal.remote.selectedLoanProduct?.provider === FinanceType.ENFIN
  const isEnfinTpo =
    proposal.local.fundingGroup === FundingGroup.EnfinTpo ||
    (isEnfin && proposal.remote.selectedLoanProduct?.loanType === `ppa`)
  const isExternalContractFlow = isTpoProductSelected || isConcert || isEnfinTpo

  const proposalId = proposal.remote.id
  // Only a provider-owned main contract can have a supplemental package Vision
  // sends, so a Vision-handled proposal never needs this read.
  const { data: contractPackage } = useContractPackage(
    isExternalContractFlow ? proposalId : undefined,
  )
  const retrySupplementalSend = useRetrySupplementalSend(proposalId, {
    onSettled: refetchContracts,
  })
  // Which mutation is in flight, and its error, derived during render — no
  // mirrored state and no effect to keep in sync.
  const pendingSupplementalAttemptId = retrySupplementalSend.isLoading
    ? (retrySupplementalSend.variables ?? null)
    : null

  const failedSupplementalAction = retrySupplementalSend.isError
    ? retrySupplementalSend
    : null

  const supplementalActionError = describeSupplementalActionError(
    failedSupplementalAction?.error,
  )
  // Attach the error to the package it was raised for, so a second failed
  // package does not show someone else's message.
  const supplementalActionErrorAttemptId =
    failedSupplementalAction?.variables ?? null

  const pendingVersionAlert = buildPendingVersionAlert({
    contracts,
    proposalTotalCost,
    remoteDesign,
  })

  const handleSendContracts = async (): Promise<void> => {
    await requestContract()
  }

  const handleNavigateToFinance = useCallback((): void => {
    setJumpTo(`financialSummary`)
    navigate(`/proposal/${slug}`)
  }, [navigate, setJumpTo, slug])

  const errorMessages = useMemo(() => {
    const messages: ReactNode[] = []

    if (isExternalContractFlow) {
      messages.push(
        <span key="external-contract-flow">
          Primary contracts are sent automatically by your financing provider.
          Vision sends supplemental documents after the provider confirms its
          contract was sent. Retry or resend supplemental documents here. Use
          {` `}
          <span
            onClick={handleNavigateToFinance}
            className="cursor-pointer underline"
          >
            Finance Status
          </span>
          {` `}
          to manage the provider’s main contract.
        </span>,
      )
    }

    if (!isExternalContractFlow && poisonedDesign) {
      messages.push(<span key="design">{t(`designNeedsCorrected`)}</span>)
    }

    if (!isExternalContractFlow && poisonedQuote) {
      messages.push(<span key="quote">{poisonedQuote.reason}</span>)
    }

    if (!isExternalContractFlow && contractReadiness.missingSystemSize) {
      messages.push(
        <span key="systemSize">
          Design must have panels placed before sending contracts.
        </span>,
      )
    }

    if (!isExternalContractFlow && contractReadiness.missingPrimaryInverter) {
      messages.push(
        <span key="inverter">
          Design must have an inverter selected before sending contracts.
        </span>,
      )
    }

    return messages
  }, [
    isExternalContractFlow,
    poisonedDesign,
    poisonedQuote,
    contractReadiness,
    handleNavigateToFinance,
    t,
  ])

  return (
    <ContractStatusModal
      isOpen={isOpen}
      onClose={onClose}
      onSendDocs={handleSendContracts}
      onResend={resendContract}
      onSendSupplemental={sendSupplementalContract}
      resendingContractId={resendingContractId}
      sendingSupplementalId={sendingSupplementalId}
      resendSuccessId={resendSuccess}
      sendSupplementalSuccessId={sendSupplementalSuccess}
      onClearResendSuccess={clearResendSuccess}
      onClearSendSupplementalSuccess={clearSendSupplementalSuccess}
      disabled={!canEdit}
      contracts={contracts}
      versionHistory={versionHistory}
      pendingVersionAlert={pendingVersionAlert}
      isLoading={loading}
      errorMessages={errorMessages}
      supplementalAttempts={contractPackage?.supplementalAttempts}
      onRetrySupplementalSend={retrySupplementalSend.mutate}
      pendingSupplementalAttemptId={pendingSupplementalAttemptId}
      supplementalActionError={supplementalActionError}
      supplementalActionErrorAttemptId={supplementalActionErrorAttemptId}
    />
  )
}
